interface HelpStep {
  title: string;
  content: string;
  tip?: string;
}

interface HelpGuide {
  [key: string]: {
    title: string;
    steps: HelpStep[];
  };
}

export const helpGuides: HelpGuide = {
  // Sales Funnels
  salesFunnels: {
    title: '📊 How to Create a Sales Funnel',
    steps: [
      {
        title: 'What is a Sales Funnel?',
        content: `A sales funnel is a step-by-step journey that guides potential customers from discovering your course to purchasing it.

<strong>Why use funnels?</strong>
• Visualize your customer journey
• Identify conversion bottlenecks
• Optimize each step for better sales
• Track performance metrics`,
        tip: 'Start by browsing the Templates tab to see examples of proven funnels used by successful course creators!'
      },
      {
        title: 'Browse Templates First',
        content: `Before creating your own funnel, explore our pre-made templates:

1. Click the <strong>"Templates"</strong> tab at the top
2. Browse funnels designed for different course types
3. Click <strong>"View Details"</strong> to see the complete funnel structure
4. Note the steps, conversion rates, and target audience

Use these as inspiration for your own funnel!`,
        tip: 'Templates show industry-standard conversion rates so you know what to expect.'
      },
      {
        title: 'Create Your First Funnel',
        content: `Ready to build? Here's how:

1. Click the <strong>"My Funnels"</strong> tab
2. Click <strong>"+ Create Funnel"</strong> button
3. Fill in the basics:
   • <strong>Funnel Name:</strong> Something memorable (e.g., "Course Launch 2024")
   • <strong>Description:</strong> What this funnel is for
   • <strong>Category:</strong> Choose your course type
   • <strong>Expected ROI:</strong> Your revenue goal`,
        tip: 'Be specific with your funnel name - you might create multiple funnels for different products!'
      },
      {
        title: 'Add Funnel Steps',
        content: `Now add the steps potential customers go through:

<strong>Example steps:</strong>
1. <strong>Awareness:</strong> Social media ad → Landing page
2. <strong>Interest:</strong> Free webinar signup
3. <strong>Decision:</strong> Email sequence (5 emails)
4. <strong>Action:</strong> Sales page → Checkout

For each step, add:
• <strong>Step Name:</strong> What happens in this step
• <strong>Description:</strong> Details about the action
• <strong>Conversion Rate:</strong> Expected % (e.g., "20%" means 20% proceed to next step)`,
        tip: 'Start with 4-6 steps. You can always add more later as you refine your funnel!'
      },
      {
        title: 'Define Your Target Audience',
        content: `Who is this funnel designed for?

Add items to the <strong>"Best For"</strong> section:
• Beginner course creators
• Health & wellness coaches
• B2B consultants
• Creative professionals
• etc.

This helps you stay focused on who you're targeting and ensures your funnel speaks to the right audience.`,
        tip: 'The more specific your target audience, the better your funnel will convert!'
      },
      {
        title: 'Save and Track Performance',
        content: `Once you've created your funnel:

1. Click <strong>"Create Funnel"</strong> to save
2. View it anytime in the "My Funnels" tab
3. Edit to update conversion rates based on real data
4. Delete funnels that aren't performing

<strong>Pro Strategy:</strong>
Create multiple funnels to test different approaches, then double down on what works!`,
        tip: 'Review and update your funnels monthly based on actual performance data.'
      }
    ]
  },

  // Course Creation with AI
  courseCreationAI: {
    title: '✨ How to Create a Course with AI',
    steps: [
      {
        title: 'Welcome to AI Course Creation!',
        content: `Our AI will help you build a complete course in minutes, not months.

<strong>What the AI creates for you:</strong>
• Complete course outline with modules
• Lesson plans and learning objectives
• Suggested activities and assessments
• Marketing copy and descriptions
• Everything you need to launch!`,
        tip: 'The more specific you are about your topic and audience, the better the AI results will be!'
      },
      {
        title: 'Step 1: Describe Your Course',
        content: `Click <strong>"✨ Create with AI"</strong> from your Dashboard.

<strong>Fill in the form:</strong>

<strong>Course Title:</strong>
Be specific and benefit-focused.
✅ Good: "Instagram Marketing for Small Businesses"
❌ Vague: "Social Media Course"

<strong>Description:</strong>
Explain what students will learn and achieve.
Include the transformation they'll experience.

<strong>Target Audience:</strong>
Who is this course for? Be specific about skill level, industry, or goals.`,
        tip: 'Write your description as if you\'re pitching to a student. Make it exciting and clear!'
      },
      {
        title: 'Step 2: AI Generates Your Course',
        content: `Click <strong>"Generate Course"</strong> and watch the magic happen!

The AI will:
1. Analyze your description
2. Research your topic
3. Create a structured outline
4. Generate module and lesson content
5. Add learning objectives

This takes 30-60 seconds. ⏳

Once complete, you'll see your full course structure!`,
        tip: 'Grab a coffee while the AI works. It\'s analyzing thousands of successful courses to create yours!'
      },
      {
        title: 'Step 3: Review and Customize',
        content: `Your AI-generated course is ready!

<strong>What to review:</strong>
• <strong>Modules:</strong> Do they flow logically?
• <strong>Lessons:</strong> Are topics covered in the right order?
• <strong>Content:</strong> Does it match your teaching style?

<strong>How to customize:</strong>
1. Click <strong>"Edit Course"</strong>
2. Reorder modules by dragging
3. Add/remove lessons as needed
4. Customize content to add your unique expertise
5. Add your own examples and case studies`,
        tip: 'The AI provides the structure. Your expertise and personality make it unique!'
      },
      {
        title: 'Step 4: Add Your Content',
        content: `Now make it yours!

<strong>What to add:</strong>
• Video lessons (record or upload)
• Worksheets and downloads
• Quizzes and assessments
• Case studies and examples
• Your personal stories and insights

<strong>Pro tip:</strong>
Start with Module 1 and create content lesson-by-lesson. Don't try to do everything at once!`,
        tip: 'Aim for "done" not "perfect". You can always improve content based on student feedback!'
      },
      {
        title: 'Step 5: Publish Your Course',
        content: `Ready to launch?

1. Click <strong>"Publish Course"</strong>
2. Students can now enroll!
3. Share your course link on social media
4. Send to your email list
5. Track enrollments from your Dashboard

<strong>After publishing:</strong>
• Monitor student progress
• Respond to questions
• Update content based on feedback
• Market your course to grow enrollment`,
        tip: 'Don\'t wait for perfection. Launch with your MVP and improve based on real student feedback!'
      }
    ]
  },

  // Drip Campaigns
  dripCampaigns: {
    title: '📧 How to Set Up Drip Campaigns',
    steps: [
      {
        title: 'What are Drip Campaigns?',
        content: `Drip campaigns are automated email sequences that nurture leads and convert them into students.

<strong>Perfect for:</strong>
• Welcome sequences for new subscribers
• Course launch sequences
• Re-engagement campaigns
• Upselling to existing students
• Testimonial requests

<strong>Why automate?</strong>
Set it once, and it runs on autopilot, building relationships while you sleep!`,
        tip: 'Start with a simple 3-5 email welcome sequence. You can get fancy later!'
      },
      {
        title: 'Plan Your Campaign',
        content: `Before creating, answer these questions:

<strong>1. What's the goal?</strong>
• Welcome new subscribers?
• Sell a course?
• Re-engage inactive users?

<strong>2. Who's the audience?</strong>
• New subscribers
• Free tier users
• Past students

<strong>3. How many emails?</strong>
• Welcome: 3-5 emails
• Launch: 7-10 emails
• Re-engagement: 2-3 emails`,
        tip: 'Map out your entire sequence on paper before creating it in the system!'
      },
      {
        title: 'Create Your Campaign',
        content: `Ready to build!

1. Click <strong>"+ Create Campaign"</strong>
2. Fill in the details:
   • <strong>Campaign Name:</strong> Internal reference (e.g., "Welcome Series 2024")
   • <strong>Description:</strong> What this campaign does
   • <strong>Target Tier:</strong> Who receives these emails (Free, Basic, Premium, All)
   • <strong>Trigger:</strong> When does it start? (User signup, course purchase, etc.)

3. Click <strong>"Create Campaign"</strong>`,
        tip: 'Use descriptive names! You\'ll thank yourself later when you have multiple campaigns.'
      },
      {
        title: 'Add Emails to Your Sequence',
        content: `Now add individual emails:

1. Click <strong>"Add Email"</strong>
2. For each email, set:
   • <strong>Subject Line:</strong> Compelling and clear
   • <strong>Email Body:</strong> Your message content
   • <strong>Delay:</strong> When to send (e.g., "Send immediately", "Wait 2 days", "Wait 1 week")

<strong>Example sequence:</strong>
• Email 1: Welcome + What to expect (Send immediately)
• Email 2: Your first lesson (Wait 1 day)
• Email 3: Case study (Wait 3 days)
• Email 4: Special offer (Wait 5 days)`,
        tip: 'Write all your emails in a Google Doc first, then copy/paste. It\'s faster!'
      },
      {
        title: 'Write High-Converting Emails',
        content: `<strong>Email Formula:</strong>

<strong>Subject Line:</strong>
• Keep it under 50 characters
• Create curiosity or urgency
• Personalize when possible

<strong>Email Body:</strong>
1. <strong>Hook:</strong> Grab attention in first line
2. <strong>Value:</strong> Teach or share something useful
3. <strong>CTA:</strong> Clear next step (e.g., "Enroll now", "Watch lesson 1")

<strong>Best Practices:</strong>
• Keep emails short (200-300 words)
• One email = One goal
• Use stories and examples
• Always include a clear CTA`,
        tip: 'Your first email should set expectations: "Over the next week, I\'ll send you 5 emails about..."'
      },
      {
        title: 'Activate and Monitor',
        content: `Launch your campaign:

1. Review all emails for typos
2. Click <strong>"Activate Campaign"</strong>
3. Confirm activation

<strong>Monitoring:</strong>
• Check subscriber count
• Track open rates (coming soon)
• Monitor conversions
• Pause campaign anytime if needed

<strong>Optimization:</strong>
After 100+ sends, review performance and update emails that aren't converting.`,
        tip: 'Start with one campaign. Once it\'s working, create more for different segments!'
      }
    ]
  },

  // Branding
  branding: {
    title: '🎨 How to Customize Your Brand',
    steps: [
      {
        title: 'Why Branding Matters',
        content: `Your brand is your visual identity. It makes your courses instantly recognizable and professional.

<strong>What you can customize:</strong>
• Primary, secondary, and accent colors
• Font pairs (heading + body)
• Logo
• Brand style across all courses

<strong>Consistency = Professional</strong>
When everything looks cohesive, students trust you more!`,
        tip: 'Choose colors and fonts that match your existing website or social media for consistency!'
      },
      {
        title: 'Choose Your Colors',
        content: `Click on the color pickers to select your brand colors:

<strong>Primary Color:</strong>
Your main brand color (buttons, headers, links)

<strong>Secondary Color:</strong>
Complements your primary (backgrounds, accents)

<strong>Accent Color:</strong>
For CTAs, highlights, important elements

<strong>Color Tips:</strong>
• Use high contrast for readability
• Keep it simple (2-3 colors max)
• Test on different screens`,
        tip: 'Not sure what colors to use? Try coolors.co to generate beautiful palettes!'
      },
      {
        title: 'Select Your Fonts',
        content: `Choose fonts that reflect your brand personality:

<strong>Heading Font:</strong>
Bold, attention-grabbing (e.g., Montserrat, Poppins, Playfair)

<strong>Body Font:</strong>
Clean, easy to read (e.g., Open Sans, Lato, Inter)

<strong>Font Pairing Tips:</strong>
• Contrast is key (serif + sans-serif works great)
• Limit to 2 fonts max
• Ensure readability on mobile

Try different combinations until it feels right!`,
        tip: 'Google "font pairing examples" for inspiration. Or stick with our pre-made pairs!'
      },
      {
        title: 'Upload Your Logo',
        content: `Add your logo to make courses truly yours:

1. Click <strong>"Upload Logo"</strong>
2. Choose your logo file (PNG or JPG)
3. <strong>Logo Requirements:</strong>
   • Transparent background (PNG recommended)
   • At least 200px wide
   • Square or horizontal orientation works best

Your logo will appear on:
• Course pages
• Certificates
• Exported materials`,
        tip: 'Don\'t have a logo? Use Canva to create one for free in minutes!'
      },
      {
        title: 'Preview Your Brand',
        content: `See your brand in action:

1. Scroll down to the <strong>"Preview"</strong> section
2. See how your colors, fonts, and logo look together
3. Make adjustments if needed

<strong>What to check:</strong>
• Is text readable on colored backgrounds?
• Do colors complement each other?
• Does your logo stand out?
• Does it match your overall vibe?`,
        tip: 'Show your preview to a friend and ask: "Does this look professional?" Trust their gut!'
      },
      {
        title: 'Save and Apply',
        content: `Love your brand? Let's save it!

1. Click <strong>"Save Branding"</strong>
2. Your brand is now applied to:
   • All existing courses
   • New courses you create
   • Marketing materials
   • Email templates
   • Certificates

<strong>Update Anytime:</strong>
You can change your branding whenever you want. All courses update automatically!`,
        tip: 'Take before/after screenshots to see the transformation. It\'s really satisfying!'
      }
    ]
  },

  // Freebie Vault
  freebieVault: {
    title: '🎁 How to Use the Freebie Vault',
    steps: [
      {
        title: 'Welcome to the Freebie Vault!',
        content: `Access hundreds of resources to help you create and market your courses.

<strong>What's inside:</strong>
• Course templates and planners
• Marketing guides and checklists
• Email swipe files
• Worksheets and tools
• Social media templates

<strong>All ready to use!</strong> Download, customize, and implement immediately.`,
        tip: 'Browse all categories first to see what\'s available. You might find gems you didn\'t know you needed!'
      },
      {
        title: 'Browse by Category',
        content: `Use the category filters to find what you need:

<strong>📋 Templates:</strong> Course outlines, lesson plans, calendars
<strong>📘 Guides:</strong> How-to guides, strategy PDFs
<strong>📝 Swipe Files:</strong> Email templates, sales page copy
<strong>📊 Worksheets:</strong> Planning tools, customer avatars
<strong>🛠️ Tools:</strong> Tech comparisons, roadmaps

Click any category button to filter. Click <strong>"All"</strong> to see everything.`,
        tip: 'Start with the "Templates" category - these are the most actionable resources!'
      },
      {
        title: 'Download Freebies',
        content: `Found something useful?

1. Click the freebie card to see details
2. Read the description to confirm it\'s what you need
3. Click <strong>"Download"</strong>

<strong>Download Limits by Tier:</strong>
• <strong>Free:</strong> Browse only (upgrade to download)
• <strong>Basic:</strong> 5 downloads per month
• <strong>Premium/Lifetime:</strong> Unlimited downloads

Your download count resets monthly!`,
        tip: 'Choose wisely if you\'re on the Basic plan. Download what you\'ll use immediately!'
      },
      {
        title: 'Customize and Use',
        content: `After downloading:

<strong>For Templates:</strong>
• Open in Google Docs, Word, or Excel
• Replace placeholder text with your info
• Customize colors and branding
• Save and use!

<strong>For Guides:</strong>
• Read and take notes
• Implement strategies immediately
• Bookmark for future reference

<strong>For Swipe Files:</strong>
• Copy the structure/framework
• Customize with your voice and details
• Never use copy verbatim - make it yours!`,
        tip: 'Create a "Freebies" folder on your computer to keep everything organized!'
      },
      {
        title: 'Track Your Downloads',
        content: `Keep track of what you've downloaded:

<strong>On the Freebies page:</strong>
• See your current download count
• Know how many downloads you have left (Basic tier)
• Downloaded freebies show a checkmark

<strong>Running low on downloads?</strong>
Upgrade to Premium for unlimited access to all resources!`,
        tip: 'Upgrade during a launch or busy month when you need lots of resources fast!'
      },
      {
        title: 'Request New Freebies',
        content: `Don't see what you need?

We're constantly adding new resources!

<strong>What to do:</strong>
• Check back monthly for new additions
• Watch for email announcements
• Suggest resources you'd like to see

<strong>Coming soon:</strong>
More swipe files, advanced templates, and exclusive tools for Premium members!`,
        tip: 'Follow us on social media to get notified when new freebies drop!'
      }
    ]
  },

  // Marketing Materials
  marketingMaterials: {
    title: '📝 How to Generate Marketing Materials',
    steps: [
      {
        title: 'AI-Powered Marketing Copy',
        content: `Generate professional marketing materials in seconds!

<strong>What we can create:</strong>
• Email sequences (launch, welcome, promo)
• Social media posts (Instagram, LinkedIn, Twitter)
• Sales page copy
• Ad copy for Facebook/Google
• Course descriptions

All powered by AI, customized for YOUR course!`,
        tip: 'The AI learns from thousands of high-converting marketing examples!'
      },
      {
        title: 'Select Your Content Type',
        content: `Choose what you want to create:

<strong>Popular options:</strong>
• <strong>Email Sequence:</strong> 5-7 automated emails
• <strong>Social Media Bundle:</strong> 10 posts for Instagram/Facebook
• <strong>Sales Page:</strong> Full landing page copy
• <strong>Ad Copy:</strong> Headlines + body for paid ads
• <strong>Course Description:</strong> SEO-optimized description

Click the type you need to get started!`,
        tip: 'Start with an email sequence - it\'s the fastest way to see results!'
      },
      {
        title: 'Provide Course Details',
        content: `Help the AI understand your course:

<strong>Fill in:</strong>
• <strong>Course Name:</strong> Your course title
• <strong>Target Audience:</strong> Who it's for
• <strong>Main Benefit:</strong> What transformation do students get?
• <strong>Key Features:</strong> What makes your course unique?
• <strong>Tone:</strong> Professional, casual, energetic, etc.

The more specific you are, the better the results!`,
        tip: 'Write like you\'re explaining it to a friend. That authenticity comes through!'
      },
      {
        title: 'Review Generated Content',
        content: `The AI generates your marketing materials!

<strong>What you'll receive:</strong>
• Multiple variations to choose from
• Ready-to-use copy
• Structured and formatted
• Optimized for conversions

<strong>Review for:</strong>
• Accuracy (does it match your course?)
• Tone (does it sound like you?)
• CTAs (are they clear?)`,
        tip: 'AI is great, but always add your personal touch and real stories!'
      },
      {
        title: 'Customize and Export',
        content: `Make it yours:

1. Edit any part of the generated copy
2. Add your personal stories and examples
3. Adjust CTAs to match your goals
4. Click <strong>"Copy to Clipboard"</strong> or <strong>"Export"</strong>

<strong>Where to use:</strong>
• Paste into your email platform
• Schedule social media posts
• Add to your sales page
• Upload to ad platforms`,
        tip: 'Save all generated copy in a Google Doc. You might repurpose it later!'
      },
      {
        title: 'Test and Optimize',
        content: `Launch and learn:

<strong>Best practices:</strong>
• Test different variations
• Track what converts best
• A/B test subject lines
• Monitor engagement metrics

<strong>Regenerate anytime:</strong>
Come back and create new variations whenever you need fresh content!

<strong>Pro tip:</strong>
What works for one course might not work for another. Keep testing!`,
        tip: 'Save your highest-performing copy as templates for future courses!'
      }
    ]
  },

  // Dashboard Overview
  dashboard: {
    title: '🏠 Dashboard Overview',
    steps: [
      {
        title: 'Welcome to Your Dashboard!',
        content: `Your command center for course creation and management.

<strong>From here you can:</strong>
• View all your courses
• Create new courses
• Access all features
• Manage your account
• Track your progress

Everything you need is just one click away!`,
        tip: 'Bookmark your dashboard - you\'ll visit it often!'
      },
      {
        title: 'Quick Actions Section',
        content: `The colorful cards at the top are your quick actions:

<strong>Create Course:</strong> ✨ Start a new course with AI
<strong>Templates:</strong> 📚 Browse ready-made course templates
<strong>Branding:</strong> 🎨 Customize your brand colors and fonts
<strong>Email Campaigns:</strong> 📧 Create email sequences
<strong>Marketing:</strong> 📝 Generate marketing materials

Click any card to jump straight to that feature!`,
        tip: 'New users: Start with "Create Course" to build your first course in minutes!'
      },
      {
        title: 'Your Courses Section',
        content: `Below quick actions, you'll see "My Courses":

<strong>If you have courses:</strong>
• Grid view of all your courses
• Click any course to edit it
• See course status (Draft/Published)
• Track creation date

<strong>If you're new:</strong>
• You'll see options to create your first course
• Choose AI creation, templates, or manual

All your courses live here!`,
        tip: 'Organize courses by prefixing names: "[DRAFT]", "[LIVE]", "[ARCHIVED]"'
      },
      {
        title: 'Additional Features',
        content: `More powerful features available:

<strong>Basic+ Tier Access:</strong>
• 📧 <strong>Drip Campaigns:</strong> Automated email sequences
• 🎁 <strong>Freebie Vault:</strong> Download resources (5/month for Basic, unlimited for Premium)
• 📊 <strong>Sales Funnels:</strong> Create custom sales funnels

<strong>Premium+ Features:</strong>
• 🔗 <strong>Integrations:</strong> Connect to email platforms, LMS, payment processors
• 👥 <strong>Team Members:</strong> Collaborate with others
• 📊 <strong>Advanced Analytics:</strong> Track student progress and engagement`,
        tip: 'Upgrade anytime from the pricing page to unlock more features!'
      },
      {
        title: 'Account Settings',
        content: `Manage your account:

<strong>Click your profile icon (top right):</strong>
• <strong>Profile:</strong> Update your name and email
• <strong>Billing:</strong> Manage subscription and payment
• <strong>Settings:</strong> Preferences and notifications
• <strong>Logout:</strong> Secure sign out

<strong>Need to upgrade?</strong>
Click <strong>"Upgrade"</strong> in the banner to see pricing and features!`,
        tip: 'Keep your email updated - that\'s where we send important notifications!'
      },
      {
        title: 'Get Help Anytime',
        content: `Stuck? We've got you covered!

<strong>Help Resources:</strong>
• <strong>❓ Help Buttons:</strong> On every page (look for the purple ? icon)
• <strong>💬 Chat Support:</strong> Bottom right corner (Tawk widget)
• <strong>📧 Email:</strong> support@templimizeai.com
• <strong>📚 Guides:</strong> Step-by-step walkthroughs (you're reading one now!)

<strong>We're here to help you succeed!</strong> Never hesitate to reach out.`,
        tip: 'Most questions are answered in our help guides. Try those first for instant answers!'
      }
    ]
  },

  // Email Templates
  emailTemplates: {
    title: '📧 How to Create Email Templates',
    steps: [
      {
        title: 'What are Email Templates?',
        content: `Save time by creating reusable email templates for common situations.

<strong>Use templates for:</strong>
• Course welcome emails
• Weekly newsletters
• Promotional emails
• Student check-ins
• Testimonial requests
• Upsell offers

Create once, use forever!`,
        tip: 'Build a library of 5-10 templates for your most common emails!'
      },
      {
        title: 'Create a New Template',
        content: `Let's build your first template:

1. Click <strong>"+ Create Template"</strong>
2. Fill in the form:
   • <strong>Template Name:</strong> Internal label (e.g., "Welcome Email")
   • <strong>Subject Line:</strong> Your email subject
   • <strong>Email Body:</strong> The message content

<strong>Use placeholders:</strong>
• {{name}} - Student's first name
• {{course}} - Course title
• {{date}} - Current date

These auto-fill when you send!`,
        tip: 'Use clear template names so you can find them quickly later!'
      },
      {
        title: 'Write Effective Emails',
        content: `<strong>Email Structure:</strong>

<strong>1. Subject Line:</strong>
• Keep under 50 characters
• Create curiosity or value
• Personalize: "{{name}}, here's your first lesson!"

<strong>2. Opening:</strong>
• Greet personally: "Hey {{name}}!"
• Reference context

<strong>3. Body:</strong>
• One main point per email
• Use short paragraphs
• Add value (teach, inspire, help)

<strong>4. Call-to-Action:</strong>
• Clear next step
• Make it obvious and easy`,
        tip: 'Read your email out loud. If it sounds robotic, rewrite it!'
      },
      {
        title: 'Use Your Templates',
        content: `Your templates are ready to use!

<strong>To send a template:</strong>
1. Go to Email Campaigns
2. Create a new campaign
3. Click <strong>"Use Template"</strong>
4. Select your saved template
5. Customize if needed
6. Send!

<strong>Templates save you:</strong>
• Time (no rewriting from scratch)
• Consistency (same quality every time)
• Mental energy (one less decision)`,
        tip: 'Create templates during your "creative" time, use them during "doing" time!'
      },
      {
        title: 'Edit and Improve',
        content: `Keep your templates fresh:

<strong>Regular maintenance:</strong>
• Update templates quarterly
• Test different subject lines
• Refresh examples and links
• Remove outdated info

<strong>Track performance:</strong>
• Which templates get the best open rates?
• Which drive the most clicks?
• Which lead to sales?

Double down on winners!`,
        tip: 'Version your templates: "Welcome Email v2" when you make big changes!'
      },
      {
        title: 'Build Your Template Library',
        content: `<strong>Essential templates to create:</strong>

1. <strong>Welcome:</strong> New student onboarding
2. <strong>Weekly:</strong> Regular check-ins
3. <strong>Promo:</strong> Course launch or sale
4. <strong>Engagement:</strong> Re-activate inactive students
5. <strong>Testimonial:</strong> Request reviews
6. <strong>Upsell:</strong> Promote advanced courses
7. <strong>Support:</strong> Helpful resources

<strong>Start with 3-5, build from there!</strong>`,
        tip: 'Steal the structure from emails you love receiving. Make it your own!'
      }
    ]
  },

  // Integrations
  integrations: {
    title: '🔗 How to Connect Integrations',
    steps: [
      {
        title: 'Why Integrate?',
        content: `Connect your favorite tools to work seamlessly together!

<strong>Available integrations:</strong>
• Email platforms (Mailchimp, ConvertKit, etc.)
• Payment processors (Stripe, PayPal)
• LMS platforms (Teachable, Thinkific)
• Analytics (Google Analytics)
• Webinar tools (Zoom)
• And more!

<strong>Benefits:</strong>
• Automated workflows
• Centralized data
• Time savings
• Better student experience`,
        tip: 'Start with email and payment integrations - they\'re the most important!'
      },
      {
        title: 'Connect Your First Integration',
        content: `Let's connect an integration:

1. Browse available integrations
2. Click <strong>"Connect"</strong> on the tool you want
3. You'll be redirected to authorize the connection
4. Log in to that platform
5. Grant permissions
6. You'll be redirected back - connected! ✅

<strong>Security:</strong>
We use OAuth for secure connections. We never see your passwords!`,
        tip: 'Have your login credentials ready before starting the connection process!'
      },
      {
        title: 'Configure Integration Settings',
        content: `After connecting, configure how it works:

<strong>For Email Platforms:</strong>
• Choose which list to sync
• Set up automation triggers
• Map student fields

<strong>For Payment Processors:</strong>
• Set default currency
• Configure webhook URL
• Test transaction

<strong>For LMS:</strong>
• Map course fields
• Set sync frequency
• Choose what data to export`,
        tip: 'Test with a small group first before rolling out to all students!'
      },
      {
        title: 'Set Up Automations',
        content: `Make integrations work automatically:

<strong>Example automations:</strong>

<strong>When student enrolls:</strong>
→ Add to email list
→ Send welcome email
→ Create account in LMS
→ Send login credentials

<strong>When course completes:</strong>
→ Send certificate
→ Request testimonial
→ Offer advanced course
→ Update CRM status

Set it and forget it!`,
        tip: 'Map out your ideal student journey, then build automations to support it!'
      },
      {
        title: 'Monitor and Troubleshoot',
        content: `Keep integrations running smoothly:

<strong>Check integration status:</strong>
• Green = Working perfectly
• Yellow = Needs attention
• Red = Disconnected (re-authorize)

<strong>Common issues:</strong>
• <strong>Disconnected:</strong> Re-authorize the connection
• <strong>Not syncing:</strong> Check field mappings
• <strong>Error messages:</strong> Read the error and follow instructions

<strong>Activity logs show:</strong>
• Recent syncs
• Errors and warnings
• Data transferred`,
        tip: 'Check your integrations monthly to catch issues before students notice!'
      },
      {
        title: 'Disconnect and Manage',
        content: `Need to make changes?

<strong>To disconnect:</strong>
1. Find the integration
2. Click <strong>"Disconnect"</strong>
3. Confirm disconnection
4. Your data remains safe!

<strong>To update settings:</strong>
1. Click <strong>"Settings"</strong> on the integration
2. Adjust configurations
3. Save changes

<strong>Premium features:</strong>
• Connect unlimited integrations
• Advanced automation workflows
• Priority support for integration issues`,
        tip: 'Before disconnecting, export any data you want to keep!'
      }
    ]
  }
};
